// ═══════════════════════════════════════════════════════════════════════════
// Dashboards Registry
// ───────────────────────────────────────────────────────────────────────────
// Índice central de todos los dashboards (vistas de datos) disponibles.
// Agregar un dashboard nuevo = agregar una entrada acá + crear su .jsx.
//
// Cada entrada declara:
//   - id              : string único usado en la URL (/dashboards/:id) y en el registry
//   - nombre          : nombre visible en la card del hub
//   - descripcion     : copete explicativo en la card
//   - plataforma      : id del canal (mercado-libre, tienda-nube, amazon, etc.)
//                       Debe matchear el id de CANALES_DEFAULT en src/lib/constants.js
//   - tipo            : 'acciones' | 'informacion'
//                       'acciones'   → tiene secciones que generan tareas (cards
//                                       seleccionables + AccionableBar + modal).
//                       'informacion' → solo lee y muestra datos / análisis.
//                       El badge "IA disponible" se deriva automáticamente del
//                       registry de clasificadores (`dashboardsConClasificador`).
//   - icono           : emoji legacy (fallback si no hay Icon)
//   - Icon            : componente lucide-react (preferido, AI/futurista vibe)
//   - component       : () => import('./path/Dashboard.jsx')  — lazy import
//   (requiereConexion se ELIMINÓ en F2-T3 — ADR 2026-08-16 decisión 5:
//   19 declaraciones, cero consumidores desde su introducción. Si el
//   empty-state "conectá una cuenta" se quiere, se reintroduce CON consumidor.)
//   - orden           : (opcional) para ordenar dentro de una plataforma
//   - proximamente    : (opcional) true = card visible pero sin destino aún
//                       (no navega, sin `component`). Muestra chip "Próximamente".
//   - altoVolumen     : (opcional) true cuando el dashboard quedó ESCALADO
//                       (Bloque 7, ADR 016): rangos server-side + aggregation
//                       API, apto cuentas 20k+ publicaciones/órdenes. Activa
//                       el chip "Alto volumen" + degradé glass en la card.
//                       Se prende dashboard por dashboard al migrarlo — NO
//                       prender de antemano.
//   - soloStaff       : (opcional) true = flag de rollout: el dashboard solo
//                       existe para staff (esStaff de permisos.js) — ni card
//                       ni acceso por URL para clientes. Plan Cuadro General
//                       §6.1: deploy = push a main sin paso manual, así que el
//                       flag es la única forma de mergear una vista a medias
//                       sin exponerla. Se apaga con OK de producto.
// ═══════════════════════════════════════════════════════════════════════════

import {
  Layout, Grid2x2, TrendingUp, Zap, Package, MapPin,
  ShoppingCart, Megaphone, Wallet, Tags, MessageSquare, Star, RotateCw, BookOpen, Radar, Activity, FlaskConical, Boxes,
  LayoutDashboard,
} from 'lucide-react'

// PLATAFORMAS y los helpers de agregación viven en el registro NEUTRO
// `src/core/lib/dashboardsRegistro.js` (ADR 2026-08-16, decisión 1): este
// archivo queda como la DECLARACIÓN de los dashboards del módulo ML, que
// `config.js` expone en `dashboards:` para que el agregador los junte.
export const DASHBOARDS = [
  {
    // Plan Cuadro General (docs/PLAN_cuadro-general-reportes.md §6, PR-2a):
    // la foto completa del negocio en UNA vista — NO calcula nada nuevo, CITA
    // los mismos rollups/fuentes que los dashboards de detalle (§10 paridad).
    // Idioma cliente desde el día 1 (§1.3). Rollout staff-only TERMINADO
    // (17-jul-2026, habilitación del portal del Owner): ver va por permiso
    // dashboards.ver (gate en el propio componente + vistas de empresa);
    // editar (entitlement mercado) sigue siendo solo-staff desde gestión.
    // orden -1 = arriba de Mi Posición.
    // Nombre visible "Pulso general" (17-jul, Mariano) — el id interno sigue
    // ml-cuadro-general a propósito: renombrar ids/archivos/permisos antes
    // del merge es churn sin valor para el usuario.
    id: 'ml-cuadro-general',
    nombre: 'Pulso general',
    descripcion: 'El pulso del negocio en una sola vista: ventas, abastecimiento, rentabilidad, publicaciones, publicidad, logística y postventa, cada área con su semáforo.',
    // Magenta de marca (17-jul, Mariano: "se sabe que eso es nuestro") — el
    // mismo rosa del tag Universo Milbrands. Override por dashboard (como el
    // azul de Mi Posición): ícono, glow y chip lo heredan solos.
    accent: '#ec4899',
    plataforma: 'mercado-libre',
    familia: 'general',
    tipo: 'informacion',
    icono: '🧭',
    Icon: LayoutDashboard,
    component: () => import('./dashboards/CuadroGeneralDashboard.jsx'),
    orden: -1,
  },
  {
    // ADR 047 — Mi Posición: PRIMERA en la grilla (orden 0, decisión Mariano:
    // "es el dashboard que te dice dónde mirar; los otros te dicen qué hacer").
    // `accent` azul = convención "dato de mercado" (Market Pro) para distinguir
    // de la operación propia; `chip` lo hace explícito en la card.
    id: 'ml-posicion',
    nombre: 'Mi Posición',
    descripcion: 'Tu share y crecimiento contra cada categoría donde competís: dónde ganás, dónde perdés y qué palanca mover.',
    plataforma: 'mercado-libre',
    // Familia PROVISORIA (30-sep-2026): mira la competencia, y la familia
    // "Competencia" sólo para marketplaces está sin decidir por Mariano.
    familia: 'ventas',
    tipo: 'informacion',
    icono: '📡',
    Icon: Radar,
    accent: '#378ADD',
    chip: 'Mercado',
    component: () => import('./dashboards/MiPosicionDashboard.jsx'),
    orden: 0,
    altoVolumen: true, // nace escalado: lee el rollup precalculado __posicion (ADR 016/047)
  },
  {
    // ADR 2026-09-01 — Packs y Combos: sugiere publicar la variante xN de un
    // producto (señal pack: 2+ unidades en la misma orden) o dos productos como
    // combo (señal: mismo carrito 3+ veces). Solo-rollup (`__combos`, cron con
    // flag feature.mercado.combos) ⇒ nace escalado. `soloStaff` al nacer: se
    // abre a clientes con OK de producto (y ahí "aceptar → tarea" necesita su
    // propio callable, limitación declarada en el diseño §3.3).
    id: 'ml-packs-combos',
    nombre: 'Packs y Combos',
    descripcion: 'Qué productos se compran de a varios y cuáles se compran juntos: sugerencias de packs y combos para publicar, y lo que ya tenés publicado.',
    plataforma: 'mercado-libre',
    familia: 'catalogo',
    tipo: 'acciones',
    icono: '📦',
    Icon: Boxes,
    component: () => import('./dashboards/PacksCombosDashboard.jsx'),
    orden: 2.5,
    altoVolumen: true,
    soloStaff: true,
  },
  {
    id: 'ml-publicaciones',
    nombre: 'Publicaciones',
    descripcion: 'El listado de publicaciones: estado, precio, stock y ventas, con acceso directo a cada una en ML.',
    plataforma: 'mercado-libre',
    familia: 'catalogo',
    tipo: 'informacion',
    icono: '📋',
    Icon: Layout,
    component: () => import('./pages/PublicacionesPage.jsx'),
    orden: 1,
    altoVolumen: true,  // B4.4 (6-jun-2026) — count() en vivo + rollup + listado paginado server tras feature.publicaciones.rollup
  },
  {
    id: 'ml-catalogo',
    nombre: 'Catálogo',
    descripcion: 'Competencia en catálogo: qué productos catalogados están ganando y qué tenés que ajustar para ganar.',
    plataforma: 'mercado-libre',
    // Familia PROVISORIA (30-sep-2026): mira la competencia, y la familia
    // "Competencia" sólo para marketplaces está sin decidir por Mariano.
    familia: 'precios-promociones',
    tipo: 'acciones',
    icono: '📚',
    Icon: BookOpen,
    component: () => import('./dashboards/CatalogoDashboard.jsx'),
    orden: 2,
    altoVolumen: true,  // t5 — lee el rollup precalculado (dashboardRollups/{emp}__catalogo);
    // el dashboard se auto-gatea por feature.catalogo.rollup (OFF = preview).
  },
  {
    id: 'ml-matriz-distribucion',
    nombre: 'Matriz de distribución de publicaciones',
    descripcion: 'Clasifica cada publicación en Estrella, Moderada, Eventual o Incógnita según su rendimiento vs. el promedio, para saber dónde enfocar.',
    plataforma: 'mercado-libre',
    familia: 'ventas',
    tipo: 'informacion',
    icono: '🎯',
    Icon: Grid2x2,
    component: () => import('./dashboards/MatrizDistribucionDashboard.jsx'),
    orden: 3,
    altoVolumen: true,  // Bloque 7.7 — ordenes + publicaciones canonicas
  },
  {
    id: 'ml-evolucion-matriz',
    nombre: 'Evolución de la matriz de publicaciones',
    descripcion: 'Cómo se movió cada publicación entre cuadrantes en los últimos meses: cuáles subieron, cuáles cayeron y cuáles se mantienen.',
    plataforma: 'mercado-libre',
    familia: 'ventas',
    tipo: 'informacion',
    icono: '📈',
    Icon: TrendingUp,
    component: () => import('./dashboards/EvolucionMatrizDashboard.jsx'),
    orden: 4,
    altoVolumen: true,  // Bloque 7.7 — ordenes + publicaciones canonicas
  },
  {
    id: 'ml-velocidad-consumo',
    nombre: 'Velocidad de consumo',
    descripcion: 'Mide la velocidad de venta diaria de cada publicación y proyecta en cuántos días se agota el stock, para reponer a tiempo.',
    plataforma: 'mercado-libre',
    familia: 'abastecimiento',
    tipo: 'acciones',
    icono: '⚡',
    Icon: Zap,
    component: () => import('./dashboards/VelocidadConsumoDashboard.jsx'),
    orden: 5,
    altoVolumen: true,  // Bloque 7.4 — ordenes + publicaciones canónicas
  },
  {
    id: 'ml-descarga-full',
    nombre: 'Estrategia Full',
    descripcion: 'Gestioná tu stock en Full: qué conviene enviar, qué ítems lentos arriesgan cargos por almacenamiento y cuánto pagaste el último período.',
    plataforma: 'mercado-libre',
    familia: 'abastecimiento',
    tipo: 'informacion',
    icono: '📦',
    Icon: Package,
    component: () => import('./dashboards/DescargaFullDashboard.jsx'),
    orden: 6,
    altoVolumen: true,  // Bloque 7.7 — ordenes + publicaciones canonicas (Full FIFO sigue legacy, wave propia)
  },
  {
    id: 'ml-flex-zonas',
    nombre: 'Estrategia Flex',
    descripcion: 'Tu situación en Flex: cobertura y oportunidades por zona, horarios y feriados, cruzado con tus ventas reales.',
    plataforma: 'mercado-libre',
    familia: 'operacion',
    tipo: 'informacion',
    icono: '📍',
    Icon: MapPin,
    component: () => import('./dashboards/EstrategiaFlexDashboard.jsx'),
    orden: 7,
    altoVolumen: true,  // Bloque 7.7 — ordenes canonicas via puente (tabs Flex)
  },
  {
    id: 'ml-ventas',
    nombre: 'Ventas',
    descripcion: 'El detalle de cada venta en un solo lugar: comprador, monto, estado, envío, pagos y los mensajes de post-venta.',
    plataforma: 'mercado-libre',
    familia: 'ventas',
    tipo: 'informacion',
    icono: '🛒',
    Icon: ShoppingCart,
    component: () => import('./pages/VentasPage.jsx'),
    orden: 10,
    altoVolumen: true,  // Bloque 7.2 — ordenes canónica, rango server-side
  },
  {
    // Mapa de demanda ML y Geo Targeting (8-oct-2026, diseño en el Project):
    // dónde están los compradores y qué compran ahí, calculado en el servidor
    // (rollup __geo, flag feature.mi-negocio.geo por empresa). Sin la llave el
    // tablero muestra «todavía no se calculó» y el botón Sincronizar dice que
    // no está habilitada: no hace falta esconder la card.
    id: 'ml-mapa-demanda',
    nombre: 'Mapa de demanda',
    descripcion: 'Dónde están tus compradores y qué te compran en cada zona: provincias, localidades y códigos postales, con lo que hay para mirar en cada una contra toda la cuenta.',
    plataforma: 'mercado-libre',
    familia: 'ventas',
    tipo: 'informacion',
    icono: '📍',
    Icon: MapPin,
    component: () => import('./dashboards/MapaDemandaMlDashboard.jsx'),
    orden: 10.5,
    altoVolumen: true,  // lee el rollup __geo del servidor: cero órdenes en el navegador
  },
  {
    id: 'ml-tendencias',
    nombre: 'Tendencias de demanda',
    descripcion: 'Keywords en tendencia en tus categorías cruzadas con tus publicaciones: sin stock, sin publicidad, baja calidad, perdiendo buy-box y oportunidades sin cubrir.',
    plataforma: 'mercado-libre',
    familia: 'trafico-conversion',
    tipo: 'acciones',
    icono: '📈',
    Icon: TrendingUp,
    component: () => import('./dashboards/TendenciasDashboard.jsx'),
    orden: 9,
  },
  {
    // ADR 049 — Tráfico y Conversión. Entre Tendencias (9) y Ventas (10).
    // Distinto de Publicidad: mira orgánico vs pago y el embudo visita→venta;
    // la eficiencia de campañas (ACOS/ROAS/CTR) se queda en ml-publicidad.
    id: 'ml-trafico-conversion',
    nombre: 'Tráfico y Conversión',
    descripcion: 'Cómo llega el tráfico a cada publicación y qué tan bien convierte: orgánico vs pago, conversión total/orgánica/ads, concentración y las pubs que quedan sin visitas.',
    plataforma: 'mercado-libre',
    familia: 'trafico-conversion',
    tipo: 'acciones',
    icono: '📊',
    Icon: Activity,
    component: () => import('./dashboards/TraficoConversionDashboard.jsx'),
    orden: 9.5,
    // NO altoVolumen: tajada 1 lee en el cliente; se prende al migrar al rollup (ADR 049 tajada 2).
  },
  {
    id: 'ml-publicidad',
    nombre: 'Publicidad (Product Ads)',
    descripcion: 'El rendimiento de tus campañas de Product Ads: inversión, clicks, CTR, CPC y ROAS, con el detalle por publicación.',
    plataforma: 'mercado-libre',
    familia: 'trafico-conversion',
    tipo: 'acciones',
    icono: '📢',
    Icon: Megaphone,
    component: () => import('./dashboards/PublicidadDashboard.jsx'),
    orden: 8,
    altoVolumen: true,  // Bloque 7.7 — ordenes + publicaciones + visitas canonicas (ads sigue legacy, WAVE-N)
  },
  {
    id: 'ml-contribucion-neta',
    // Nombres normalizados con Tienda Nube (30-sep-2026, Mariano: "vamos a
    // normalizar los nombres de los dashboards entre Mercado Libre y Tienda
    // Nube"): el mismo dashboard se llama igual en los dos canales, con
    // mayúscula sólo inicial. Antes: "Contribución Neta por Orden". También:
    // Velocidad de consumo, las dos Matrices y Promociones (ex "Central de
    // Promociones"). Los ids NO cambian — son la clave de permisos y tareas.
    nombre: 'Rentabilidad por orden',
    descripcion: 'Cuánto ganás de verdad en cada orden: cruza la venta con costo de producto y publicidad para estimar tu contribución neta.',
    plataforma: 'mercado-libre',
    familia: 'rentabilidad',
    tipo: 'informacion',
    icono: '💰',
    Icon: Wallet,
    component: () => import('./dashboards/ContribucionNetaDashboard.jsx'),
    orden: 11,
    altoVolumen: true,  // Bloque 7.3 — ordenes canónica, rango server-side
  },
  {
    // Dashboard VIOLETA (15-jul-2026): universo DISJUNTO de Contribución Neta
    // — pubs SIN venta en la ventana, rentabilidad INFERIDA por cascada (lib
    // rentabilidadEstimada, fuente declarada). Completo: vista + tareas
    // (subir_precio al precio sugerido, 2 urgencias) + agente 16
    // (agente-rentabilidad-inferida-ml). Contrato: automatizacion-deteccion-tareas.md §6.
    id: 'ml-rentabilidad-inferida',
    nombre: 'Rentabilidad inferida',
    descripcion: 'Para lo que todavía no vende: infiere el margen de cada publicación con la fórmula completa (comisión, envío, impuestos) y marca cuáles venderían a pérdida o bajo tu mínima.',
    plataforma: 'mercado-libre',
    familia: 'rentabilidad',
    tipo: 'acciones',
    icono: '🧪',
    Icon: FlaskConical,
    accent: '#a78bfa',
    chip: 'Inferida',
    component: () => import('./dashboards/RentabilidadInferidaDashboard.jsx'),
    orden: 11.5,
  },
  {
    id: 'ml-central-promociones',
    nombre: 'Promociones',
    descripcion: 'Todas las promociones de ML en un tablero: cuáles están activas o disponibles, qué ítems participan, cuánto ponés vos vs. ML y los deadlines.',
    plataforma: 'mercado-libre',
    familia: 'precios-promociones',
    tipo: 'informacion',
    icono: '🏷️',
    Icon: Tags,
    component: () => import('./dashboards/CentralPromocionesDashboard.jsx'),
    orden: 9,
  },
  {
    id: 'ml-preguntas-respuestas',
    nombre: 'Preguntas y Respuestas',
    descripcion: 'Preguntas, respuestas: volumen, tiempo de respuesta y conversión de pregunta a venta, con acciones para mejorar cobertura y contenido.',
    plataforma: 'mercado-libre',
    familia: 'postventa',
    tipo: 'acciones',
    icono: '💬',
    Icon: MessageSquare,
    component: () => import('./dashboards/PreguntasRespuestasDashboard.jsx'),
    orden: 12,
    altoVolumen: true,  // Bloque 7.7 — ordenes canonicas con rango server-side
  },
  {
    id: 'ml-opiniones',
    nombre: 'Opiniones de productos',
    descripcion: 'La voz de tus compradores por publicación: rating, estrellas y tendencia, con elogios para destacar y quejas para corregir en la ficha.',
    plataforma: 'mercado-libre',
    familia: 'postventa',
    tipo: 'acciones',
    icono: '⭐',
    Icon: Star,
    component: () => import('./dashboards/OpinionesDashboard.jsx'),
    orden: 13,
  },
  {
    id: 'ml-postventa',
    nombre: 'Postventa',
    descripcion: 'Reclamos, devoluciones y reputación bajo control: reglas con umbrales que sugieren pausar o ajustar publicaciones y abrir casos.',
    plataforma: 'mercado-libre',
    familia: 'postventa',
    tipo: 'acciones',
    icono: '🔄',
    Icon: RotateCw,
    component: () => import('./dashboards/PostventaDashboard.jsx'),
    altoVolumen: true,  // Bloque 7.5 — count/curva/cascada sobre ordenes canónica (lado reclamos sigue legacy DEP-1)
    orden: 14,
  },
  // Agregar acá cada dashboard nuevo.
]
